import { type FC, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../components/Button/Button'
import { LinkButton } from '../../components/Button/LinkButton'
import { IntegrationIcon } from '../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../common/integrations'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { addToast } from '../../store/toast/toastSlice'
import { useGetMeQuery, useGetUserIntegrationInfoQuery } from '../../store/user/user'
import { useGetUserSchedulingSettingsQuery, useUpdateUserSchedulingSettingsMutation } from '../../store/scheduling/scheduling'
import { type CalendarProvider, MeetingPlatformProvider } from '../../models/Scheduling'
import {
  CALENDAR_OPTIONS,
  getIntegrationConnection,
  getSchedulingCapability,
  IntegrationConnectionStatus,
  resolveIntegrationsOnCalendarSelect,
} from '../ProfileSettings/Scheduling/sections/schedulingIntegrationStatus'
import { OnboardingStepHeader } from '../Growth/Onboarding/OnboardingStepHeader/OnboardingStepHeader'

const INTEGRATION_SETTINGS_PATH = '/account/integration-settings/integrations'

export const SelectCalendarState: FC = () => {
  const dispatch = useAppDispatch()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const { data: me } = useGetMeQuery({ accountId })
  const { data: integrationInfo } = useGetUserIntegrationInfoQuery({ accountId, userId: authenticatedUser.id })
  const { data: userSettings } = useGetUserSchedulingSettingsQuery({ accountId, userId: authenticatedUser.id })
  const [updateUserSettings, { isLoading: isUpdating }] = useUpdateUserSchedulingSettingsMutation()
  const [chosen, setChosen] = useState<CalendarProvider>()

  if (userSettings == null) {
    return null
  }

  const options = CALENDAR_OPTIONS.map((option) => ({
    ...option,
    connection: getIntegrationConnection(
      me?.integrations,
      integrationInfo?.data.problems,
      option.serviceId,
      getSchedulingCapability(userSettings.integrations, option.provider),
    ),
  })).filter((option) => option.connection.status === IntegrationConnectionStatus.READY)

  const chosenProvider = chosen ?? options[0]?.provider
  const chosenOption = options.find((option) => option.provider === chosenProvider)

  const isZoomSelectable =
    getIntegrationConnection(
      me?.integrations,
      integrationInfo?.data.problems,
      IntegrationServiceId.ZOOM,
      getSchedulingCapability(userSettings.integrations, MeetingPlatformProvider.ZOOM),
    ).status === IntegrationConnectionStatus.READY

  const selectCalendar = async (provider: CalendarProvider) => {
    const result = await updateUserSettings({
      accountId,
      userId: authenticatedUser.id,
      update: { integrations: resolveIntegrationsOnCalendarSelect(userSettings.integrations, provider, isZoomSelectable) },
    })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not select the calendar', messages: ['Please try again.'] }))
    }
  }

  return (
    <div className='border zocks-border-color-gray-300 rounded-4 d-flex flex-column align-items-center gap-4 zocks-py-5.0 px-3'>
      <OnboardingStepHeader
        icon={<i className='bi bi-calendar2' />}
        title='Choose your scheduling calendar'
        subtitle='Scheduling adds booked meetings to one calendar. Choose which one to use.'
      />
      <Row className='justify-content-center g-0 w-100'>
        <Col xs={12} md={8} lg={6} xl={5} className='d-flex flex-column align-items-center gap-4'>
          <div role='radiogroup' aria-label='Scheduling calendar' className='d-flex flex-column gap-2 w-100'>
            {options.map((option) => {
              const isSelected = option.provider === chosenProvider
              return (
                <button
                  key={option.serviceId}
                  type='button'
                  role='radio'
                  aria-checked={isSelected}
                  aria-label={option.label}
                  onClick={() => setChosen(option.provider)}
                  className={classNames(
                    'd-flex align-items-center gap-2 p-3 rounded-3 text-start border zocks-bg-color-white',
                    isSelected ? 'zocks-border-color-primary' : 'zocks-border-color-gray-300',
                  )}
                >
                  <i className={isSelected ? 'bi bi-record-circle-fill zocks-text-primary' : 'bi bi-circle zocks-text-gray-500'} />
                  <IntegrationIcon serviceId={option.serviceId} width='1.5rem' height='1.5rem' />
                  <span className='d-flex flex-column overflow-hidden'>
                    <span className='paragraph-small bold zocks-text-gray-900'>{option.label}</span>
                    <span className='d-flex align-items-center paragraph-small'>
                      <span className='d-flex align-items-center zocks-gap-0.25 zocks-text-success'>
                        <i className='bi bi-check' />
                        Connected
                      </span>
                      {option.connection.email ? (
                        <>
                          <i className='bi bi-dot zocks-text-gray-600' />
                          <span className='zocks-text-gray-600 text-truncate'>{option.connection.email}</span>
                        </>
                      ) : null}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
          {chosenOption ? (
            <Button variant='primary' className='w-100' loading={isUpdating} onClick={() => void selectCalendar(chosenOption.provider)}>
              Select calendar
            </Button>
          ) : null}
          <LinkButton variant='primaryLinkButton' to={INTEGRATION_SETTINGS_PATH} className='text-decoration-none'>
            Manage your calendar integrations
          </LinkButton>
        </Col>
      </Row>
    </div>
  )
}
